/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DeviceApiService {
  private history = [];
  public img = '';
  previousUrl;
  private APIService = environment.serviceUrl;

  constructor(
    private http: HttpClient,
    public snackBar: MatSnackBar,
    private router: Router
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.history.push(event.urlAfterRedirects);
      }
    });
  }

  getProductionData(
    currentPage,
    pageSize,
    search,
    selectedDeviceModelId,
    selectedCustomerId
  ): Observable<any> {
    return this.http.get(
      this.APIService +
        'device/getAllDeviceDetailsList?page=' +
        currentPage +
        '&size=' +
        pageSize +
        '&search=' +
        search +
        '&selectedDeviceModelId=' +
        selectedDeviceModelId +
        '&selectedCustomerId=' +
        selectedCustomerId
    );
  }

  getDispatchedData(
    currentPage,
    pageSize,
    search,
    selectedDeviceModelId,
    selectedCustomerId
  ): Observable<any> {
    return this.http.get(
      this.APIService +
        'device/getAllDispatchDeviceDetailsList?page=' +
        currentPage +
        '&size=' +
        pageSize +
        '&search=' +
        search +
        '&selectedDeviceModelId=' +
        selectedDeviceModelId +
        '&selectedCustomerId=' +
        selectedCustomerId
    );
  }

  getInstalledDevices(
    currentPage,
    pageSize,
    search,
    selectedDeviceModelId,
    selectedCustomerId
  ): Observable<any> {
    return this.http.get(
      this.APIService +
        'device/getAllInstalledDeviceDetailsList?page=' +
        currentPage +
        '&size=' +
        pageSize +
        '&search=' +
        search +
        '&selectedDeviceModelId=' +
        selectedDeviceModelId +
        '&selectedCustomerId=' +
        selectedCustomerId
    );
  }

  getDiscardedDevices(
    currentPage,
    pageSize,
    search,
    selectedDeviceModelId,
    selectedCustomerId
  ): Observable<any> {
    return this.http.get(
      this.APIService +
        'device/getAllDiscardedDeviceDetailsList?page=' +
        currentPage +
        '&size=' +
        pageSize +
        '&search=' +
        search +
        '&selectedDeviceModelId=' +
        selectedDeviceModelId +
        '&selectedCustomerId=' +
        selectedCustomerId
    );
  }

  fetchDeviceActivityOverviewData(
    currentPage,
    pageSize,
    search,
    inactiveDuration,
    selectedDeviceModelId,
    selectedCustomerId
  ): Observable<any> {
    return this.http.get(
      this.APIService +
        'device/getInactiveDeviceList?page=' +
        currentPage +
        '&size=' +
        pageSize +
        '&search=' +
        search +
        '&inactiveDuration=' +
        inactiveDuration +
        '&selectedDeviceModelId=' +
        selectedDeviceModelId +
        '&selectedCustomerId=' +
        selectedCustomerId
    );
  }

  getFirmwareWiseDeviceList(
    currentPage,
    pageSize,
    search,
    firmwareVersion,
    firmwareType,
    selectedDeviceModelId
  ): Observable<any> {
    return this.http.get(
      this.APIService +
        'deviceCurrentState/getFirmwareWiseDeviceList?page=' +
        currentPage +
        '&size=' +
        pageSize +
        '&search=' +
        search +
        '&firmwareVersion=' +
        firmwareVersion +
        '&firmwareType=' +
        firmwareType +
        '&selectedDeviceModelId=' +
        selectedDeviceModelId
    );
  }

  countAllDeviceDetails(
    selectedDeviceModelId,
    selectedCustomerId
  ): Observable<any> {
    const productionCount$ = this.http
      .get<{
        data;
      }>(
        this.APIService +
          'device/getProductionDeviceCount?selectedDeviceModelId=' +
          selectedDeviceModelId +
          '&selectedCustomerId=' +
          selectedCustomerId
      )
      .pipe(
        catchError((error) => {
          console.error('Production count API failed:', error);
          return of({ data: 0 }); // Fallback value
        })
      );
    const dispatchedCount$ = this.http
      .get<{
        data;
      }>(
        this.APIService +
          'device/getDispatchDeviceCount?selectedDeviceModelId=' +
          selectedDeviceModelId +
          '&selectedCustomerId=' +
          selectedCustomerId
      )
      .pipe(
        catchError((error) => {
          console.error('Dispatch count API failed:', error);
          return of({ data: 0 });
        })
      );
    const installedCount$ = this.http
      .get<{
        data;
      }>(
        this.APIService +
          'device/getInstalledDeviceCount?selectedDeviceModelId=' +
          selectedDeviceModelId +
          '&selectedCustomerId=' +
          selectedCustomerId
      )
      .pipe(
        catchError((error) => {
          console.error('Installed count API failed:', error);
          return of({ data: 0 });
        })
      );
    const discardedCount$ = this.http
      .get<{
        data;
      }>(
        this.APIService +
          'device/getDiscardedDeviceCount?selectedDeviceModelId=' +
          selectedDeviceModelId +
          '&selectedCustomerId=' +
          selectedCustomerId
      )
      .pipe(
        catchError((error) => {
          console.error('Discarded count API failed:', error);
          return of({ data: 0 });
        })
      );

    return forkJoin({
      production: productionCount$,
      dispatched: dispatchedCount$,
      installed: installedCount$,
      discarded: discardedCount$,
    }).pipe(
      map((res) => ({
        status: true,
        productionDevice: res.production.data,
        dispatchedDevice: res.dispatched.data,
        installedDevice: res.installed.data,
        discardedDevice: res.discarded.data,
      }))
    );
  }

  inActiveGraph(selectedDeviceModelId, selectedCustomerId): Observable<any> {
    return this.http.get(
      this.APIService +
        'device/getInactiveDeviceCount?selectedDeviceModelId=' +
        selectedDeviceModelId +
        '&selectedCustomerId=' +
        selectedCustomerId
    );
  }

  getFirmwareWiseDeviceCount(
    selectedDeviceModelId,
    selectedCustomerId
  ): Observable<any> {
    return this.http.get(
      this.APIService +
        'deviceCurrentState/getFirmwareWiseDeviceCount?selectedDeviceModelId=' +
        selectedDeviceModelId +
        '&selectedCustomerId=' +
        selectedCustomerId
    );
  }

  getDeviceCurrentState(imei: string): Observable<any> {
    return this.http.get(
      this.APIService + 'deviceCurrentState/getDeviceCurrentState?imei=' + imei
    );
  }
}
